export type ParseMaybeDoubleEncodedJSONOptions = {
  /**
   * Maximum number of decode passes for double-encoded JSON strings.
   * Defaults to 2.
   */
  maxDepth?: number;
  /**
   * What to return when a string looks like JSON but JSON.parse fails.
   * - "original": keep the original input value (best-effort)
   * - "undefined": return undefined (strict parsing)
   *
   * Defaults to "original".
   */
  onParseError?: 'original' | 'undefined';
};

function looksLikeJSON(s: string): boolean {
  const trimmed = s.trim();
  return (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  );
}

export function parseMaybeDoubleEncodedJSON(
  value: unknown,
  opts?: ParseMaybeDoubleEncodedJSONOptions
): unknown {
  const maxDepth = opts?.maxDepth ?? 2;
  const onParseError = opts?.onParseError ?? 'original';

  let current: unknown = value;
  for (let i = 0; i < maxDepth; i++) {
    if (typeof current !== 'string') break;
    if (!looksLikeJSON(current)) break;

    try {
      current = JSON.parse(current.trim());
    } catch {
      return onParseError === 'undefined' ? undefined : value;
    }
  }
  return current;
}
